import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import { FileUploader } from 'react-drag-drop-files';

import progressAnimation from '@/assets/animation/save_loader.json';
import { ReactComponent as UploadFileIcon } from '@/assets/img/v2/upload-file-icon.svg';
import DatePicker, { DayValue, Day } from 'react-modern-calendar-datepicker';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import { ReactComponent as CalendarIcon } from '@/assets/img/v2/calendar-icon.svg';

import { Button } from '@/components/Elements';
import {
  useAddPetInsurance,
  useGetPetInsurance,
  useUploadDocument,
  useDeleteDocument,
} from '../api/addPetInsurance';
import Lottie from 'lottie-react';

import moment from 'moment';
import './PetInsuranceForm.scss';
import { showNotification } from '@/utils/notificationUtils';
import Form from 'react-bootstrap/Form';
import CommonCheckbox from './CommonCheckBox';
import CommonFileUpload from './CommonUpload';
import { PetskedTitle } from '@/components/Heading/PetskedTitle';
import { InsuranceCard } from './InsuranceCard';
import { Loader } from '@/components/Loader/Loader';
import ConfirmationDialogV2 from '@/components/ConfirmationDialog/ConfirmationDialogV2';
import { useTranslation } from 'react-i18next';
type Props = {
  petId: number;
};

export type Insurance = {
  id?: string;
  petId?: number;
  insurance: boolean;
  insuranceName?: string;
  insuranceExpiry?: string;
  insuranceInformation?: string;
  insuranceCopy?: string;
  ownership: string;
  vaccineRecords: string;
};

export const PetInsuranceForm = ({ petId }: Props) => {
  const addPetInsuranceMutation = useAddPetInsurance();
  const getPetInsuranceMutation = useGetPetInsurance();
  const uploadPetDocumentMutation = useUploadDocument();
  const deletePetDocumentMutation = useDeleteDocument();
  const [insuranceData, setInsuranceData] = useState<Insurance>();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const [day, setDay] = React.useState<Day>({
    day: moment().date(),
    month: moment().month() + 1,
    year: moment().year(),
  } as Day);
  useEffect(() => {
    getInsurance();
  }, []);
  const getInsurance = async () => {
    const res = await getPetInsuranceMutation.mutateAsync({
      data: { petId },
    });
    setInsuranceData(res);
  };

  const validate = () => {
    if (insuranceData) {
      const { insuranceName, insuranceExpiry, insuranceInformation } = insuranceData;
      if (!insuranceName || insuranceName.trim().length < 1) {
        showNotification({
          type: 'info',
          title: t('Please enter insurance name'),
        });
        return false;
      } else if (!insuranceExpiry || insuranceExpiry.trim().length < 1) {
        showNotification({
          type: 'info',
          title: t('Please enter insurance expiriation date'),
        });
        return false;
      } else if (!insuranceInformation || insuranceInformation.trim().length < 1) {
        showNotification({
          type: 'info',
          title: t('Please enter insurance information'),
        });
        return false;
      }
      return true;
    }
  };

  const onFileSelect = (file: File) => {
    if (file.size > 10000000) {
      showNotification({
        type: 'info',
        title: t('Uploaded image should be less than 5 mb .'),
      });
      return false;
    }
    uploadPetDocuments(file);
  };
  const uploadPetDocuments = async (selectedFiles: any) => {
    if (selectedFiles) {
      const formData = new FormData();
      formData.append('file', selectedFiles);
      formData.append('type', 'insuranceCopy');
      formData.append('petId', petId + '');
      const response = await uploadPetDocumentMutation.mutateAsync({
        file: formData,
      });
      getInsurance();
    }
  };
  const deleteInsurance = async () => {
    if (insuranceData) {
      const file = insuranceData?.insuranceCopy && insuranceData?.insuranceCopy.split(/\//)[3];
      setShowDeleteConfirmation(false);
      await deletePetDocumentMutation.mutateAsync({
        data: {
          id: insuranceData.id ? insuranceData.id : '',
          file: file ? file : '',
          type: 'insuranceCopy',
        },
      });
      getInsurance();
    }
  };
  const renderCustomInput = ({ ref }: any) => (
    <input
      readOnly
      ref={ref} // necessary
      placeholder={t("Select Date")}
      value={
        insuranceData?.insuranceExpiry
          ? moment(insuranceData.insuranceExpiry).format('DD-MMM-YYYY')
          : ''
      }
      className="cust-input-mobile" // a styling class
    />
  );
  const saveInsuranceDetails = async () => {
    if (validate() && insuranceData && !addPetInsuranceMutation.isLoading) {
      const addedData = await addPetInsuranceMutation.mutateAsync({
        data: {
          id: insuranceData?.id ? Number(insuranceData.id) : undefined,
          petId,
          insurance: insuranceData.insuranceCopy ? true : false,
          insuranceName: insuranceData?.insuranceName,
          insuranceExpiry: insuranceData?.insuranceExpiry,
          insuranceInformation: insuranceData?.insuranceInformation,
        },
      });
      getInsurance();
    }
  };
  const onFormValueChange = (type: string, value: string) => {
    if (insuranceData) {
      if (type === 'insuranceName') {
        insuranceData.insuranceName = value;
      }
      if (type === 'insuranceInformation') {
        insuranceData.insuranceInformation = value;
      }
      if (type === 'insuranceExpiry') {
        insuranceData.insuranceExpiry = value;
      }

      setInsuranceData({ ...insuranceData });
    }
  };
  const onConfirmDeleteDoc = () => {
    deleteInsurance();
  };
  return (
    <div className="insurance-form tab-content petsked-form">
      <div className="insurance-top-section">
        <PetskedTitle
          size="sm"
          title={t("Insurance Document")}
          description=""
          alignText="left"
        />
        <span className={`status ${insuranceData?.insuranceCopy ? 'submitted' : ''}`}>
          {insuranceData?.insuranceCopy ? t('Submitted') : t('Not Submitted')}
        </span>
      </div>
      <FileUploader handleChange={onFileSelect} name="file" types={['PDF']}>
        <div className="file-upload">
          {uploadPetDocumentMutation.isLoading ? (
            <Loader text="uploading..." />
          ) : (
            <>
              <UploadFileIcon />
              <p>
                {t('Drag & drop or')} <span>{t('choose files')}</span> {t('to upload')}
              </p>
              <p>{t('Only')} <b>{t('PDF')}</b> {t('files of max')} <b>{t('10MB')}</b> {t('size allowed')}</p>
            </>
          )}
        </div>
      </FileUploader>

      {/* <input
        type="file"
        onChange={onFileSelect}
        ref={fileInputRef}
        hidden
        placeholder="Attribute name"
      /> */}

      {insuranceData?.insuranceCopy && (
        <>
          <div className="cards">
            <InsuranceCard data={insuranceData} onDelete={() => setShowDeleteConfirmation(true)} />
          </div>
          <div className="form-field">
            <label className="form-label">{t('Enter insurance details below')}</label>
            <div className="split full">
              <input
                type="text"
                placeholder={t("Details")}
                value={insuranceData.insuranceName}
                onChange={(e) => {
                  onFormValueChange('insuranceName', e.target.value);
                }}
              />
              <span className="datepicker-container">
                <DatePicker
                  maximumDate={day}
                  inputClassName="my-custom-input"
                  renderInput={renderCustomInput}
                  onChange={(val: DayValue) => {
                    onFormValueChange('insuranceExpiry', `${val?.month}/${val?.day}/${val?.year}`);
                  }}
                />
                <CalendarIcon />
              </span>
            </div>
            <label className="form-label">{t('Add some extra details below')}</label>

            <textarea
              rows={4}
              placeholder={t("Add note")}
              value={insuranceData.insuranceInformation}
              onChange={(e) => {
                onFormValueChange('insuranceInformation', e.target.value);
              }}
            />
            <div className="action">
              <button className="petsked-button button-dark" onClick={saveInsuranceDetails}>
                {addPetInsuranceMutation.isLoading ||
                uploadPetDocumentMutation.isLoading ||
                deletePetDocumentMutation.isLoading ? (
                  <Loader />
                ) : (
                  <span>{t('Save Details')}</span>
                )}
              </button>
            </div>
          </div>
        </>
      )}
      <ConfirmationDialogV2
        title={t('Remove Insurance Document?')}
        description={t('Do you really want to remove the document?')}
        yesButtonLabel={t("Yes, Confirm")}
        noButtonLabel={t("No, Not Now")}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={onConfirmDeleteDoc}
        open={showDeleteConfirmation}
      >
        {<></>}
      </ConfirmationDialogV2>
    </div>
  );
};
